import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import Draggable from 'react-draggable-component';
import Arrows from './Arrows.jsx';
import SmallAdCard from './cards/small/SmallAdCard.jsx';
import SmallImageCard from './cards/small/SmallImageCard.jsx';
import SmallQuoteCard from './cards/small/SmallQuoteCard.jsx';
import SmallTextCard from './cards/small/SmallTextCard.jsx';
import SmallLargeTextCard from './cards/small/SmallLargeTextCard.jsx';
import SmallTitleCard from './cards/small/SmallTitleCard.jsx';
import SmallTOCCard from './cards/small/SmallTOCCard.jsx';
import SmallVideoCard from './cards/small/SmallVideoCard.jsx';
import SmallInlineVideoCard from './cards/small/SmallInlineVideoCard.jsx';
import SmallProgressBar from './cards/small/SmallProgressBar.jsx';
import SmallAnnouncementCard from './cards/small/SmallAnnouncementCard.jsx';
import SmallCreditsCard from './cards/small/SmallCreditsCard.jsx';
import actions from './../actions/small-card-actions';


class SmallCardFramework extends React.Component {
  constructor(props) {
    super(props);


    // used by React as the component name
    this.displayName = 'SmallCardFramework';


    // the iteration of the current content
    this.contentIteration = 0;


    // used to track how many times the user has swiped
    this.animateIteration = 0;


    // starting drag position of the content
    this.startDragPos = 0;


    // ending drag position of the content
    this.endDragPos = 0;


    // the amount of pixels the user needs to drag to trigger a transition
    this.dragThreshold = 10;


    // the number of slides above and below the current slide
    this.surroundingSlideAmount = 2;


    // timeout to reset the content positions
    this.resetPositionTimeout = 0;


    // which card was clicked
    this.clickedCard = null;


    // the amunt of time after a card is clicked before it can be clicked again
    this.resetClickTimeout = 10;


    // bind functions to component
    this.renderCards = this.renderCards.bind(this);
    this.dragStartCallback = this.dragStartCallback.bind(this);
    this.dragCallback = this.dragCallback.bind(this);
    this.dragStopCallback = this.dragStopCallback.bind(this);
    this.getCurrentCardData = this.getCurrentCardData.bind(this);
    this.animateContent = this.animateContent.bind(this);
    this.updateSlideData = this.updateSlideData.bind(this);
    this.advanceSlide = this.advanceSlide.bind(this);
    this.reverseSlide = this.reverseSlide.bind(this);
  }


  /**
   * Called when component mounts to DOM
   * @returns {undefined} undefined
   */
  componentDidMount() {
    const currentCardData = this.getCurrentCardData();

    this.node = ReactDOM.findDOMNode(this);
    this.props.dispatch(actions.setCurrentCardData(currentCardData));
    this.draggableElement = this.refs.draggable;

    window.scrollTo(0, 0);
  }


  /**
   * Called when the user starts dragging the slides
   * @param {Object} draggablePosition - the current x and y position of the
   * draggable element
   * @returns {undefined} undefined
   */
  dragStartCallback(draggablePosition) {
    clearTimeout(this.resetPositionTimeout);

    this.startDragPos = draggablePosition.y;
    this.props.dispatch(actions.setDragging(true));
  }


  /**
   * Called while the user is dragging the slides
   * @param {Object} draggablePosition - the current x and y position of the
   * draggable element
   * @returns {undefined} undefined
   */
  dragCallback(draggablePosition) {
    this.endDragPos = draggablePosition.y;

    let dif = this.startDragPos - this.endDragPos;
    if (dif < 0) {
      dif = dif * -1;
    }

    const data = this.props.data;
    const state = this.props.smallCardFrameworkState;
    const globalState = this.props.mediaLabCardFrameworkState;
    const windowSize = globalState.windowSize;
    const currentProgress = this.contentIteration / (data.length - 1);
    const newProgress = (((draggablePosition.y * -1) -
      (this.contentIteration * windowSize.height)) / windowSize.height) / (data.length - 1);
    const totalProgress = currentProgress + newProgress;

    if (dif >= 2 && !state.defaultEventsPrevented) {
      this.props.dispatch(actions.setDefaultEventsPrevented(true));
    }

    this.props.dispatch(actions.setProgress(totalProgress));
  }


  /**
   * Called when the user stops dragging the slides
   * @param {Object} draggablePosition - the current x and y position of the
   * draggable element
   * @returns {undefined} undefined
   */
  dragStopCallback(draggablePosition) {
    const data = this.props.data;
    const state = this.props.smallCardFrameworkState;
    this.endDragPos = draggablePosition.y;

    let dif = this.startDragPos - this.endDragPos;
    if (dif < 0) {
      dif = dif * -1;
    }

    if (dif > this.dragThreshold) {
      if (this.endDragPos > this.startDragPos) {
        // dragged down
        if (this.contentIteration - 1 >= 0) {
          this.contentIteration--;
          this.animateIteration--;
        }
      } else {
        // dragged up
        if (this.contentIteration + 1 < data.length) {
          this.contentIteration++;
          this.animateIteration++;
        }
      }
    } else {
      if (this.clickedCard !== 'active' && !state.cardClickPrevented) {
        this.clickedCard = this.checkForCardClick(event);

        if (this.clickedCard === 'prev') {
          this.contentIteration--;
          this.animateIteration--;
        }

        if (this.clickedCard === 'active' &&
          this.contentIteration + 1 < this.props.data.length) {
          this.contentIteration++;
          this.animateIteration++;
        }
      }
    }

    this.updateSlideData();
  }


  /**
   * Updates slide data in the store
   * @returns {undefined} undefined
   */
  updateSlideData() {
    const data = this.props.data;

    this.props.dispatch(actions.setProgress(this.contentIteration / (data.length - 1)));
    this.props.dispatch(actions.setDragging(false));
    this.props.dispatch(actions.setCurrentCardIteration(this.contentIteration));

    setTimeout(() => {
      const currentCardData = this.getCurrentCardData();
      this.props.dispatch(actions.setCurrentCardData(currentCardData));
      this.animateContent();
      this.props.dispatch(actions.setDefaultEventsPrevented(false));
    }, 10);

    setTimeout(() => {
      this.clickedCard = null;
    }, this.resetClickTimeout);
  }

  /**
   * Advances to the next slide
   * @returns {undefined} undefined
   */
  advanceSlide() {
    if (this.contentIteration + 1 < this.props.data.length) {
      this.contentIteration++;
      this.animateIteration++;
    }

    this.updateSlideData();
  }


  /**
   * Reverses to the previous slide
   * @returns {undefined} undefined
   */
  reverseSlide() {
    if (this.contentIteration - 1 >= 0) {
      this.contentIteration--;
      this.animateIteration--;
    }

    this.updateSlideData();
  }


  /**
   * Gets the current card data
   * @returns {Array<Object>} An array of data objects
   */
  getCurrentCardData() {
    const data = this.props.data;
    const currentSlideData = [];
    const extraLength = (this.contentIteration === 0) ? 1 : 0;
    let startValue = this.contentIteration - this.surroundingSlideAmount;
    let endValue = this.contentIteration +
      (this.surroundingSlideAmount + extraLength);

    if (startValue < 0) {
      startValue = 0;
    }

    if (endValue > data.length) {
      endValue = data.length;
    }

    for (let i = startValue; i < endValue; i++) {
      currentSlideData.push(data[i]);
    }

    return currentSlideData;
  }


  /**
   * Animates the content
   * @returns {undefined} undefined
   */
  animateContent() {
    const globalState = this.props.mediaLabCardFrameworkState;
    const windowSize = globalState.windowSize;
    let newPos = 0;

    window.scrollTo(0, 0);

    if (this.animateIteration > 0) {
      newPos = -((windowSize.height * this.animateIteration - 1) + 1);
    }

    this.draggableElement.setPosition({
      x: 0,
      y: newPos,
    });
  }


  /**
   * Renders cards
   * @returns {Array<ReactElement>} An array of Card components
   */
  renderCards() {
    const state = this.props.smallCardFrameworkState;
    const data = state.currentCardData;
    const cards = [];

    for (let i = 0; i < data.length; i++) {
      cards.push(this.renderCard(data[i], i));
    }

    return cards;
  }


  /**
   * Renders card
   * @param {Object} data - A data object
   * @param {Number} iteration - The iteration of the card
   * @returns {ReactElement} A Card component
   */
  renderCard(data, iteration) {
    let element;
    const globalState = this.props.mediaLabCardFrameworkState;
    const key = 'card-' + data.order;
    const style = {
      height: globalState.windowSize.height,
      top: (data.order - 1) * globalState.windowSize.height,
      width: globalState.windowSize.width,
    };

    if (data.type === 'ad') {
      element = (
        <SmallAdCard
          data={data}
          iteration={iteration}
          largeBreakpoint={this.props.largeBreakpoint}
          key={key}
          style={style}
        />
      );
    }

    if (data.type === 'image') {
      element = (
        <SmallImageCard
          data={data}
          iteration={iteration}
          key={key}
          largeBreakpoint={this.props.largeBreakpoint}
          style={style}
        />
      );
    }

    if (data.type === 'quote') {
      element = (
        <SmallQuoteCard
          data={data}
          iteration={iteration}
          key={key}
          style={style}
        />
      );
    }

    if (data.type === 'text') {
      element = (
        <SmallTextCard
          data={data}
          iteration={iteration}
          key={key}
          style={style}
        />
      );
    }

    if (data.type === 'large-text') {
      element = (
        <SmallLargeTextCard
          data={data}
          iteration={iteration}
          key={key}
          style={style}
        />
      );
    }

    if (data.type === 'title') {
      element = (
        <SmallTitleCard
          data={data}
          iteration={iteration}
          key={key}
          largeBreakpoint={this.props.largeBreakpoint}
          style={style}
        />
      );
    }

    if (data.type === 'toc') {
      element = (
        <SmallTOCCard
          data={data}
          iteration={iteration}
          key={key}
          style={style}
        />
      );
    }

    if (data.type === 'inline-video') {
      element = (
        <SmallInlineVideoCard
          autoplay={data.autoplay}
          data={data}
          iteration={iteration}
          key={key}
          style={style}
        />
      );
    }

    if (data.type === 'announcement') {
      element = (
        <SmallAnnouncementCard
          data={data}
          iteration={iteration}
          key={key}
          style={style}
        />
      );
    }

    if (data.type === 'credits') {
      element = (
        <SmallCreditsCard
          data={data}
          iteration={iteration}
          key={key}
          style={style}
        />
      );
    }

    return element;
  }


  /**
   * Checks to see if a slide was clicked
   * @param {Object} event - A mouseup or touchend event, depending on browser
   * support
   */
  checkForCardClick(event) {
    let cardClicked;
    const cardElements = this.node.getElementsByClassName('card');

    for (let i = 0; i < cardElements.length; i++) {
      const elementBounds = cardElements[i].getBoundingClientRect();
      let clickBounds = {
        x: event.clientX,
        y: event.clientY,
      };

      if (event.changedTouches) {
        clickBounds = {
          x: event.changedTouches[0].clientX,
          y: event.changedTouches[0].clientY,
        };
      }

      if (clickBounds.x > elementBounds.left &&
        clickBounds.x < elementBounds.right &&
        clickBounds.y > elementBounds.top + 70 &&
        clickBounds.y < elementBounds.bottom - 70) {
        if (cardElements[i].className.indexOf('active') > -1) {
          cardClicked = 'active';
          break;
        }
      }
    }

    return cardClicked;
  }


  /**
   * Renders component
   * @returns {ReactElement} ReactElement
   */
  render() {
    const cards = this.renderCards();
    const state = this.props.smallCardFrameworkState;
    const globalState = this.props.mediaLabCardFrameworkState;
    const windowSize = globalState.windowSize;
    const containerStyle = {
      height: windowSize.height,
      width: windowSize.width,
    };
    let positionClass = '';
    if (state.currentCardIteration === 0) {
      positionClass = ' first-card';
    }
    if (state.currentCardIteration === this.props.data.length - 1) {
      positionClass = ' last-card';
    }
    const loadingClass = state.loading ? ' loading' : '';
    const draggingClass = state.dragging ? ' dragging' : '';
    const className = 'component-small-card-framework' +
      draggingClass + loadingClass + positionClass;

    return (
      <div
        className={className}
      >
        <Draggable
          dragStartCallback={this.dragStartCallback}
          dragCallback={this.dragCallback}
          dragStopCallback={this.dragStopCallback}
          lock={'y'}
          preventDefaultEvents={state.defaultEventsPrevented}
          ref='draggable'
          style={containerStyle}
        >
          <div className='cards-wrapper'>
            {cards}
          </div>
        </Draggable>
        <SmallProgressBar
          cardAmount={this.props.data.length}
        />
        <Arrows
          downArrowHandler={this.advanceSlide}
          upArrowHandler={this.reverseSlide}
        />
      </div>
    );
  }
}


/**
 * Expected propTypes
 * @prop {Array<Object>} data - An array of json objects
 * @prop {Function} dispatch - A Redux function to dispatch events
 * @prop {Number} largeBreakpoint - A width breakpoint
 * @prop {Object} mediaLabCardFrameworkState - The state of the
 * container component
 * @prop {Object} smallCardFrameworkState - The state of the
 * CardFramework component
 */
SmallCardFramework.propTypes = {
  data: React.PropTypes.array.isRequired,
  dispatch: React.PropTypes.func,
  largeBreakpoint: React.PropTypes.number,
  mediaLabCardFrameworkState: React.PropTypes.object.isRequired,
  smallCardFrameworkState: React.PropTypes.object.isRequired,
};


export default connect(({ mediaLabCardFrameworkState, smallCardFrameworkState }) =>
  ({ mediaLabCardFrameworkState, smallCardFrameworkState }))(SmallCardFramework);



/** WEBPACK FOOTER **
 ** ./assets/js/src/components/SmallCardFramework.jsx
 **/